import React, { useState } from 'react';
import { CouponsContainer } from '@jbhi-fi/components/src/CouponsContainer';
import { getGapToDate } from '@jbhi-fi/utils/src/date/getGapToDate';
import { Coupon } from '@jbhi-fi/components/src/types';
import { AddBirthday } from '../addBirthday/AddBirthday';
import { PerksContainer } from '@jbhi-fi/components/src/PerksContainer';
import { styles } from './PerksContent.css';
import { CustomerClient } from '../../../customers/api/customer/CustomerClient';
import { CustomerApiRecord } from '../../../customers/api/customer/types';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { CUSTOMER_PREFERENCE_KEYS } from '../../../customers/constants';
import { PerksPrompt } from '../../../customers/components/alerts/perksPrompt/PerksPrompt';
import { PERKS_COUPON_SIGNUP_MESSAGING, PREFERENCE_SAVE_ERROR_MESSAGE } from '../../constants';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag/useFeatureFlag';
import { ACCOUNT_DROPDOWN_TITLE } from '@jbhi-fi/components/src/CouponsContainer/constants';

type PerksContentProps = {
  isPerks: boolean;
  customerId: string;
  coupons: Array<Coupon>;
  onApplyCoupon: (couponNumber: string) => void;
  onRemoveCoupon: (couponNumber: string) => void;
  onSetCustomer: (data: CustomerApiRecord) => void;
};

export const PerksContent = ({
  isPerks,
  customerId,
  coupons,
  onApplyCoupon,
  onRemoveCoupon,
  onSetCustomer,
}: PerksContentProps) => {
  const { customerPreferences, birthdayMonth, handleUpdateCustomerPreferences } = useCustomerContext();
  const [savePreferenceError, setSavePreferenceError] = useState(false);
  const isPerksSignupEnabled = useFeatureFlag('isHeaderPerksSignupEnabled');
  const isPerksBirthdayFormEnabled = useFeatureFlag('isHeaderPerksBirthdayFormEnabled');

  const shouldShowBirthdayField =
    !coupons?.length && isPerksBirthdayFormEnabled && customerPreferences?.shopify_perks_skipSetBirthday !== 'true';
  const shouldShowPerksSignup = isPerksSignupEnabled && customerPreferences?.perks_skipJoinPrompt !== 'true';

  const onSubmitBirthday = async (birthMonth: number) => {
    setSavePreferenceError(false);
    const { data, error } = await CustomerClient.postBirthMonth({
      customerId: customerId,
      birthMonth: birthMonth,
    });
    if (error) setSavePreferenceError(true);
    if (data) onSetCustomer(data);
  };

  const handlePreferenceUpdate = async (key: string) => {
    setSavePreferenceError(false);
    const isUpdateSuccessful = await handleUpdateCustomerPreferences(key, 'true');
    if (!isUpdateSuccessful) setSavePreferenceError(true);
    return isUpdateSuccessful;
  };

  const renderError = () => <p className={styles.preferenceError}>{PREFERENCE_SAVE_ERROR_MESSAGE}</p>;
  if (!isPerks && shouldShowPerksSignup) {
    return (
      <>
        <PerksContainer padding="small">
          <div className={styles.signupWrapper}>
            <PerksPrompt
              heading={PERKS_COUPON_SIGNUP_MESSAGING}
              variant="small"
              onSkipJoinPerks={() => handlePreferenceUpdate(CUSTOMER_PREFERENCE_KEYS.skipPerksSignup)}
            />
          </div>
        </PerksContainer>
        {savePreferenceError ? renderError() : null}
      </>
    );
  }
  if (isPerks && !birthdayMonth && shouldShowBirthdayField) {
    return (
      <>
        <PerksContainer padding="small">
          <div className={styles.signupWrapper}>
            <AddBirthday
              onSubmitBirthday={onSubmitBirthday}
              onSkipSetBirthday={() => handlePreferenceUpdate(CUSTOMER_PREFERENCE_KEYS.skipSetBirthday)}
            />
          </div>
        </PerksContainer>
        {savePreferenceError ? renderError() : null}
      </>
    );
  }

  const daysToBirthday = getGapToDate({ measure: 'days', month: birthdayMonth });
  const isCloseToBirthMonth = daysToBirthday > 0 && daysToBirthday <= 21;

  return isPerks ? (
    <CouponsContainer
      title={ACCOUNT_DROPDOWN_TITLE}
      coupons={coupons}
      isCloseToBirthMonth={isCloseToBirthMonth}
      onApplyCoupon={onApplyCoupon}
      onRemoveCoupon={onRemoveCoupon}
      shouldShowSubheading
    />
  ) : null;
};
